/**
 * Copyright (c) 2022 Animation Studio Voronezh.
 *
 * CONFIDENTIAL AND PROPRIETARY
 *
 * This work is provided "AS IS" and subject to the Prodtrack
 * Source Code License. By accessing, using, copying or modifying
 * this work you indicate your agreement to the Prodtrack Source Code License.
 * All rights not expressly granted therein are reserved by Animation Studio Voronezh.
 */

import React, { useEffect, useState } from "react";
import axios from "../../../services/axios.js";
import defaultRequest from "../../../services/api/index.js";
import handleTableError from "functions/handleTableError";
import FirstStepLogin from "./FirstStep.js";
import SecondStepLogin from "./SecondStep.js";
import { Redirect } from "react-router-dom";
import Loading from "components/Widgets/LoadingWidget";
import { toast } from "react-toastify";

export const LoginForm = () => {
    const [credentials, setCredentials] = useState(() => ({
        login: "",
        password: "",
        otp_token: "",
    }));

    const [error, setError] = useState(() => ({
        login: false,
        password: false,
        otp_token: false,
        wrongCredentials: false,
        wrongToken: false,
    }));

    const isDarkTheme = JSON.parse(localStorage.getItem("isDarkTheme"));

    const [isAuthenticating, setAuthenticating] = useState(false);
    const [isKeycloakPending, setKeycloakPending] = useState(false);

    const [isMFA, setMFA] = useState(false);
    const [isKeycloak, setKeycloak] = useState(false);

    const [stepMFA, setStepMFA] = useState(0);
    const [deviceURI, setDeviceURI] = useState(false);

    const handleChange = (e, { name, value }) =>
        setCredentials((prev) => ({ ...prev, [name]: value }));

    const clickLoginButton = () => {
        if (isAuthenticating) return;

        const { login, password, otp_token } = credentials;
        setError((prev) => {
            return stepMFA === 0
                ? {
                      ...prev,
                      login: !credentials.login,
                      password: !credentials.password,
                  }
                : {
                      ...prev,
                      otp_token: !credentials.otp_token,
                  };
        });
        handleLogin(login, password, otp_token);
    };

    const checkMFA = () =>
        defaultRequest
            .get("/api/v1/mfa_status/")
            .then(({ data }) => data.status)
            .catch((error) => {
                handleTableError(error);
            });

    const checkKeycloak = () =>
        defaultRequest
            .get("/api/v1/keycloak_status/")
            .then(({ data }) => data.status)
            .catch((error) => {
                toast.error(error.toString());
            });

    const extractSecretFromOTPAuthString = (authString) => {
        if (!authString) {
            return null;
        }
        const regex = /secret=([^&]+)/;
        const match = authString.match(regex);

        if (match && match[1]) {
            return match[1];
        } else {
            return null;
        }
    };

    useEffect(() => {
        checkMFA().then((status) => setMFA(status));
        checkKeycloak().then((status) => setKeycloak(status));
    }, []);

    const handleLogin = (login, password, otp_token) => {
        const data = {
            username: login,
            password: password,
        };
        if (stepMFA) data.otp_token = otp_token;

        const url = "/api/v1/login/";

        setAuthenticating(true);

        axios
            .post(url, data)
            .then(({ data }) => {
                if (isMFA && stepMFA === 0) {
                    setAuthenticating(false);
                    setStepMFA((prev) => prev + 1);
                    setDeviceURI(data.deviceURI ?? false);
                    setError((prev) => ({
                        ...prev,
                        login: false,
                        password: false,
                        wrongCredentials: false,
                        apiMessage: "",
                    }));
                } else window.location.replace("/projects/");
            })
            .catch((error) => {
                setAuthenticating(false);
                const apiMessage =
                    error?.response?.data?.error ??
                    error?.response?.data?.detail;
                setError((prev) => ({
                    ...prev,
                    wrongCredentials: stepMFA === 0,
                    wrongToken: stepMFA === 1,
                    apiMessage,
                }));
            });
    };

    return stepMFA === 0 ? (
        <FirstStepLogin
            isDarkTheme={isDarkTheme}
            error={error}
            credentials={credentials}
            handleChange={handleChange}
            clickLoginButton={clickLoginButton}
            isKeycloak={isKeycloak}
            isAuthenticating={isAuthenticating}
            isKeycloakPending={isKeycloakPending}
            onKeycloakClick={() => setKeycloakPending(true)}
        />
    ) : (
        <SecondStepLogin
            isDarkTheme={isDarkTheme}
            error={error}
            credentials={credentials}
            handleChange={handleChange}
            clickLoginButton={clickLoginButton}
            isAuthenticating={isAuthenticating}
            deviceURI={deviceURI}
            deviceSecret={extractSecretFromOTPAuthString(deviceURI)}
        />
    );
};

export default function CheckUserAuth(props) {
    const [{ loading, isAuth }, setAuth] = useState(() => ({
        loading: true,
        isAuth: false,
    }));

    useEffect(() => {
        defaultRequest
            .get("/api/v1/get_user_info")
            .then(() => setAuth({ loading: false, isAuth: true }))
            .catch(() => setAuth({ loading: false, isAuth: false }));
    }, []);

    return loading ? (
        <Loading />
    ) : isAuth ? (
        <Redirect to="/projects" />
    ) : (
        <LoginForm {...props} />
    );
}
